import { useCallback, useReducer, useRef, useState, type DragEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CONTRIBUTION_EXTENSIONS,
  CONTRIBUTION_FILE_KINDS,
  MAX_CONTRIBUTION_BYTES,
  MAX_CONTRIBUTION_FILES,
  type ContributionFileKind,
} from '@gwc/shared';
import { historyApi, type ContributionFileRow } from '../api/historyContributions';
import { ApiError } from '../lib/api';
import { toast } from '../lib/toast';
import { ProtectedFile } from './ProtectedFile';

interface Pending {
  localId: string;
  file: File;
  status: 'queued' | 'uploading' | 'error';
  progress: number;
  error?: string;
}

const KIND_LABEL: Record<ContributionFileKind, string> = {
  photo: 'Photo of the night',
  scorecard: 'Scorecard',
  menu: 'Menu / wine list',
  label: 'Wine label',
  spreadsheet: 'Spreadsheet',
  document: 'Document',
  other: 'Other',
};

function kindsFor(contentType: string): ContributionFileKind[] {
  if (contentType.startsWith('image/')) return ['photo', 'scorecard', 'menu', 'label', 'other'];
  if (contentType === 'application/pdf') return ['scorecard', 'menu', 'document', 'other'];
  if (contentType.includes('sheet') || contentType.includes('excel') || contentType === 'text/csv') return ['spreadsheet', 'other'];
  return ['document', 'other'];
}

const ACCEPT = CONTRIBUTION_EXTENSIONS.map((e) => `.${e}`).join(',');
const fmtBytes = (n: number) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

/**
 * Drop a shoebox on it. Any number of files, uploaded one at a time (the API
 * takes one file per request), each with live progress, a type and a caption.
 */
export function FileDropzone({
  contributionId,
  files,
  disabled,
}: {
  contributionId: string;
  files: ContributionFileRow[];
  disabled?: boolean;
}) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  // The queue lives in a ref (source of truth for the serial uploader) and is
  // mirrored into state purely so the list re-renders.
  const queue = useRef<Pending[]>([]);
  const [, bump] = useReducer((n: number) => n + 1, 0);
  const pending = queue.current;
  const mutateQueue = (fn: (q: Pending[]) => Pending[]) => {
    queue.current = fn(queue.current);
    bump();
  };
  const [dragging, setDragging] = useState(false);
  const busy = useRef(false);

  const refresh = () => qc.invalidateQueries({ queryKey: ['contribution', contributionId] });

  const pump = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    try {
      for (;;) {
        const item = queue.current.find((x) => x.status === 'queued');
        if (!item) break;
        mutateQueue((q) => q.map((x) => (x.localId === item.localId ? { ...x, status: 'uploading' } : x)));
        try {
          await historyApi.uploadFile(contributionId, item.file, {
            onProgress: (f) => mutateQueue((q) => q.map((x) => (x.localId === item.localId ? { ...x, progress: f } : x))),
          });
          mutateQueue((q) => q.filter((x) => x.localId !== item.localId));
          await refresh();
        } catch (err) {
          const message = err instanceof ApiError ? err.message : 'Upload failed';
          mutateQueue((q) => q.map((x) => (x.localId === item.localId ? { ...x, status: 'error', error: message } : x)));
        }
      }
    } finally {
      busy.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contributionId]);

  const enqueue = (list: FileList | File[]) => {
    const room = MAX_CONTRIBUTION_FILES - files.length - pending.length;
    const picked = Array.from(list).slice(0, Math.max(0, room));
    if (picked.length < list.length) toast.error(`A contribution can hold up to ${MAX_CONTRIBUTION_FILES} files`);
    const ok: Pending[] = [];
    for (const f of picked) {
      if (f.size > MAX_CONTRIBUTION_BYTES) {
        toast.error(`${f.name} is larger than ${Math.round(MAX_CONTRIBUTION_BYTES / 1024 / 1024)} MB`);
        continue;
      }
      ok.push({ localId: crypto.randomUUID(), file: f, status: 'queued', progress: 0 });
    }
    if (!ok.length) return;
    mutateQueue((q) => [...q, ...ok]);
    void pump();
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    if (e.dataTransfer.files?.length) enqueue(e.dataTransfer.files);
  };

  const patch = useMutation({
    mutationFn: ({ fileId, ...p }: { fileId: string; kind?: ContributionFileKind; caption?: string | null }) =>
      historyApi.patchFile(contributionId, fileId, p),
    onSuccess: () => void refresh(),
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Could not update file'),
  });
  const remove = useMutation({
    mutationFn: (fileId: string) => historyApi.deleteFile(contributionId, fileId),
    onSuccess: () => void refresh(),
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Could not remove file'),
  });

  return (
    <div className="space-y-4">
      {!disabled && (
        <div
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={`cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition ${
            dragging ? 'border-gold-400 bg-gold-500/10' : 'border-ink-800 hover:border-gold-600/50'
          }`}
        >
          <p className="text-sm text-cream-100">Drop files here, or click to choose</p>
          <p className="mt-1 text-xs text-cream-300/60">
            Photos of scorecards, menus and labels · PDFs · Excel/CSV · Word or text. Up to{' '}
            {Math.round(MAX_CONTRIBUTION_BYTES / 1024 / 1024)} MB each, {MAX_CONTRIBUTION_FILES} per contribution.
          </p>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.length) enqueue(e.target.files);
              e.target.value = '';
            }}
          />
        </div>
      )}

      {pending.length > 0 && (
        <ul className="space-y-2">
          {pending.map((p) => (
            <li key={p.localId} className="rounded border border-ink-800 bg-ink-900/40 p-2 text-xs">
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-cream-100">{p.file.name}</span>
                <span className="shrink-0 text-cream-300/60">
                  {p.status === 'queued' && 'Waiting…'}
                  {p.status === 'uploading' && `${Math.round(p.progress * 100)}%`}
                  {p.status === 'error' && (
                    <>
                      <span className="text-wine-300">{p.error}</span>
                      <button
                        type="button"
                        className="ml-2 underline"
                        onClick={() => {
                          mutateQueue((q) => q.map((x) => (x.localId === p.localId ? { ...x, status: 'queued', error: undefined, progress: 0 } : x)));
                          void pump();
                        }}
                      >
                        retry
                      </button>
                      <button type="button" className="ml-2 underline" onClick={() => mutateQueue((q) => q.filter((x) => x.localId !== p.localId))}>
                        remove
                      </button>
                    </>
                  )}
                </span>
              </div>
              {p.status !== 'error' && (
                <div className="mt-1 h-1 overflow-hidden rounded bg-ink-800">
                  <div className="h-full bg-gold-500 transition-all" style={{ width: `${Math.round(p.progress * 100)}%` }} />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {files.length > 0 ? (
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {files.map((f) => (
            <li key={f.id} className="flex gap-3 rounded border border-ink-800 bg-ink-900/30 p-2">
              <ProtectedFile url={f.url} contentType={f.contentType} filename={f.filename} className="h-24 w-24 shrink-0 rounded object-cover" />
              <div className="min-w-0 flex-1 space-y-1 text-xs">
                <p className="truncate text-cream-100" title={f.filename}>
                  {f.filename}
                </p>
                <p className="text-cream-300/50">{fmtBytes(f.sizeBytes)}</p>
                <select
                  className="field py-1 text-xs"
                  value={f.kind}
                  disabled={disabled || f.promoted}
                  onChange={(e) => patch.mutate({ fileId: f.id, kind: e.target.value as ContributionFileKind })}
                >
                  {kindsFor(f.contentType).map((k) => (
                    <option key={k} value={k}>
                      {KIND_LABEL[k]}
                    </option>
                  ))}
                  {!kindsFor(f.contentType).includes(f.kind) && (
                    <option value={f.kind}>{KIND_LABEL[f.kind as ContributionFileKind] ?? f.kind}</option>
                  )}
                </select>
                <input
                  className="field py-1 text-xs"
                  placeholder={f.kind === 'label' ? 'Caption — start with the wine letter, e.g. "C — Ch. Margaux"' : 'Caption (optional)'}
                  defaultValue={f.caption ?? ''}
                  disabled={disabled || f.promoted}
                  onBlur={(e) => {
                    const v = e.target.value.trim() || null;
                    if (v !== (f.caption ?? null)) patch.mutate({ fileId: f.id, caption: v });
                  }}
                />
                {!disabled && !f.promoted && (
                  <button type="button" className="text-wine-300 hover:underline" onClick={() => remove.mutate(f.id)}>
                    Remove
                  </button>
                )}
                {f.promoted && <span className="text-gold-400">In the archive</span>}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        pending.length === 0 && <p className="text-xs text-cream-300/50">No files yet.</p>
      )}
      <p className="sr-only">{CONTRIBUTION_FILE_KINDS.join(', ')}</p>
    </div>
  );
}
